/**
 * The guardrail mechanics. **The rules themselves come from the server.**
 *
 * In the Next.js app `lib/guardrail/detector.ts` was imported by the browser *and* by the API
 * route, so the warning a user saw and the rule the server enforced were literally the same
 * code. Splitting into a Python backend and a TypeScript frontend makes that impossible, and
 * the two obvious substitutes are both bad: a request per keystroke is not a live control, and
 * a second hand-written copy of the keyword list here is two places to change with one
 * eventually forgotten.
 *
 * So `GET /guardrail/config` sends the rules as data and this module supplies only the
 * mechanics — a case-insensitive substring scan, and three weight lookups summed against a
 * threshold. **There is no policy in this file.** Not a keyword, not a category, not a weight,
 * not a piece of copy: every one of those would be a value that could disagree with
 * `app/domain/guardrail/`, and the backend's own tests assert the served description matches
 * what it enforces.
 *
 * None of this enforces anything. The route re-runs the detector on the exact final string
 * regardless of what happened here — `CLAUDE.md` is explicit that the route is the control and
 * that a UI check is convenience.
 */

import type { components } from "@/api/schema";

type Schemas = components["schemas"];

export type GuardrailConfig = Schemas["GuardrailConfigResponse"];
export type PublicationRiskPolicy = Schemas["PublicationRiskPolicy"];
export type PublicationRiskLevel = Schemas["PublicationRiskLevel"];
export type PublicationContext = Schemas["PublicationContext"];
export type GenerationTier = Schemas["GenerationTier"];

export interface GuardrailMatch {
  flagged: boolean;
  matchedCategories: string[];
}

/** A stable "nothing matched" value, so a caller can use it before the config has loaded. */
export const NO_GUARDRAIL_MATCH: GuardrailMatch = { flagged: false, matchedCategories: [] };

/**
 * Case-insensitive substring scan against every configured category.
 *
 * **An absent config matches nothing**, which is the safe direction here and worth being
 * explicit about: the alternative — flagging everything while the config loads — would put an
 * unacknowledgeable warning on screen for the first second of every visit, and the server
 * rejects a flagged prompt on its own authority either way. The failure mode of this returning
 * "not flagged" wrongly is a missing warning on a page whose Generate button still 400s with
 * the real reason.
 */
export function detectSensitiveTopics(
  text: string,
  config: GuardrailConfig | undefined,
): GuardrailMatch {
  if (!config) return NO_GUARDRAIL_MATCH;

  const normalised = text.toLowerCase();
  const matched = config.categories
    .filter(({ keywords }) => keywords.some((keyword) => normalised.includes(keyword.toLowerCase())))
    .map(({ category }) => category);

  return matched.length > 0 ? { flagged: true, matchedCategories: matched } : NO_GUARDRAIL_MATCH;
}

export interface PublicationRiskInput {
  matchedCategories: string[];
  intendedPublicationContexts: PublicationContext[];
  generationTier: GenerationTier;
}

/**
 * The intended-use × subject × generation-degree matrix, applied from the served weights.
 *
 * Four lines with no judgement in them, which is the point — every number is the backend's.
 * Read `app/domain/guardrail/publication_risk.py` for what the axes mean and why the tiers
 * are weighted the way they are; nothing about that reasoning belongs in this file, because
 * a copy of it here would be a copy that could go stale.
 */
export function computePublicationRisk(
  input: PublicationRiskInput,
  policy: PublicationRiskPolicy | undefined,
): PublicationRiskLevel {
  // No subject match means nothing about this brief touches an evidence-sensitive topic, so
  // the matrix never escalates it — it only ever escalates a guardrail-flagged brief.
  if (!policy || input.matchedCategories.length === 0) return "standard";

  const subject = input.matchedCategories.some((category) =>
    policy.highSubjectCategories.includes(category),
  )
    ? policy.highSubjectWeight
    : policy.otherSubjectWeight;

  /*
   * Contexts are multiple choice, and the assessment takes the single highest-stakes one
   * rather than an average: an image also destined for an annual report carries that risk
   * whatever else it is used for. An empty list takes "unspecified"'s weight — the backend
   * makes the same choice, for the reason its `_context_weight` gives.
   */
  const contexts: PublicationContext[] =
    input.intendedPublicationContexts.length > 0
      ? input.intendedPublicationContexts
      : ["unspecified"];
  const context = Math.max(
    ...contexts.map((entry) => weightOf(policy.contextWeights, entry)),
  );

  const degree = weightOf(policy.tierWeights, input.generationTier);

  return subject + context + degree >= policy.highRiskThreshold ? "high" : "elevated";
}

/**
 * The evidence-risk control: describe mode produces a fully synthetic image, so a flagged
 * brief there needs a recorded reason as well as the acknowledgement. Edit mode starts from a
 * real photograph and is out of scope for it.
 *
 * Two lines rather than served data because there is no policy here to get out of step —
 * it is the shape of the rule, and the server enforces it independently in the route.
 */
/**
 * A weight from a served map, erring high when the key is missing.
 *
 * The backend sends every context and every tier explicitly, and has a test asserting it, so
 * a missing key means a malformed config rather than a value with a sensible default. The
 * direction of the guess is the only decision, and it is the same one the backend made when
 * it hit the equivalent case: **understating risk is the wrong failure for a control whose
 * entire purpose is to warn**, so an unknown key takes the highest weight on offer.
 */
function weightOf(weights: Record<string, number>, key: string): number {
  const weight = weights[key];
  if (weight !== undefined) return weight;
  const known = Object.values(weights);
  return known.length > 0 ? Math.max(...known) : 0;
}

export function requiresSyntheticOverrideReason(
  mode: "describe" | "edit",
  flagged: boolean,
): boolean {
  return mode === "describe" && flagged;
}

export function isSyntheticOverrideReasonProvided(reason: string): boolean {
  return reason.trim().length > 0;
}
